import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProjectQueue } from '../../store/slices/eventsSlice';

const Icon = ({ name, size = 14 }) => {
  const paths = {
    plus: <path d="M12 5v14M5 12h14" />,
    close: <path d="M18 6 6 18M6 6l12 12" />,
    settings: <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></>,
    refresh: <><path d="M3 12a9 9 0 0 1 15-6.7L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" /><path d="M3 21v-5h5" /></>,
    search: <><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></>,
    users: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
    drag: <><circle cx="9" cy="6" r="1" /><circle cx="9" cy="12" r="1" /><circle cx="9" cy="18" r="1" /><circle cx="15" cy="6" r="1" /><circle cx="15" cy="12" r="1" /><circle cx="15" cy="18" r="1" /></>,
    pin: <path d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
};

const CalendarSidebar = ({
  onClose,
  onManageCalendars,
  onQueueDragStart,
  onQueueDragEnd,
  onCrewDragStart,
  onCrewDragEnd,
  activeCrewId,
  onCrewSelect,
  hideTrades,
  onToggleHideTrades
}) => {
  const dispatch = useDispatch();
  const role = useSelector((s) => s.auth.user?.profile?.role);
  const crews = useSelector((s) => s.crews.items);
  const events = useSelector((s) => s.events.events);
  const queue = useSelector((s) => s.events.queue);
  const queueLoading = useSelector((s) => s.events.queueLoading);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ready_to_schedule');
  const [crewSearch, setCrewSearch] = useState('');
  const [activeSidebarTab, setActiveSidebarTab] = useState('crews');

  React.useEffect(() => {
    dispatch(fetchProjectQueue(statusFilter));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const selectedCrew = useMemo(
    () => (crews || []).find((crew) => String(crew._id) === String(activeCrewId)) || null,
    [crews, activeCrewId]
  );

  const projectCrewIdsByProject = useMemo(() => {
    const map = new Map();

    (events || []).forEach((ev) => {
      const projectId = ev?.project?._id || ev?.project;
      const crewId = ev?.crew?._id || ev?.crew;
      if (!projectId || !crewId) return;

      const key = String(projectId);
      if (!map.has(key)) map.set(key, new Set());
      map.get(key).add(String(crewId));
    });

    return map;
  }, [events]);

  const queueMatchesSelectedCrew = (project) => {
    if (!activeCrewId) return true;
    const selectedId = String(activeCrewId);
    const directCrewId = project?.crew?._id || project?.crew || project?.assignedCrew?._id || project?.assignedCrew;
    if (directCrewId && String(directCrewId) === selectedId) return true;

    const knownCrewIds = [
      ...(Array.isArray(project?.crewIds) ? project.crewIds : []),
      ...(Array.isArray(project?.assignedCrewIds) ? project.assignedCrewIds : [])
    ].map((id) => String(id));
    if (knownCrewIds.includes(selectedId)) return true;

    const linkedCrewIds = projectCrewIdsByProject.get(String(project?._id));
    return Boolean(linkedCrewIds && linkedCrewIds.has(selectedId));
  };

  const projectMatchesSearch = (project) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    if ((project.title || '').toLowerCase().includes(q)) return true;
    const city = project?.address?.city || '';
    const state = project?.address?.state || '';
    if (`${city} ${state}`.toLowerCase().includes(q)) return true;
    const pms = project?.projectManagers || [];
    return pms.some((pm) => {
      const first = pm?.profile?.firstName || pm?.firstName || '';
      const last = pm?.profile?.lastName || pm?.lastName || '';
      const full = `${first} ${last}`.trim().toLowerCase();
      return full.includes(q) || first.toLowerCase().includes(q) || last.toLowerCase().includes(q);
    });
  };

  const filteredQueue = (queue || [])
    .filter((p) => queueMatchesSelectedCrew(p))
    .filter((p) => projectMatchesSearch(p));

  const onDragStart = (e, project) => {
    window.__draggedQueueItem = project;
    onQueueDragStart?.(project);
    e.dataTransfer.setData('text/plain', project._id);
    e.dataTransfer.effectAllowed = 'copyMove';
  };

  // Match a crew against the search query across name, trades, people, and people's trades.
  const crewMatchesSearch = (crew) => {
    const q = crewSearch.trim().toLowerCase();
    if (!q) return true;
    if ((crew.name || '').toLowerCase().includes(q)) return true;
    const tradeHit = (crew.trades || []).some((t) => (t.name || '').toLowerCase().includes(q));
    if (tradeHit) return true;
    return (crew.people || []).some((p) => {
      if ((p.name || '').toLowerCase().includes(q)) return true;
      return (p.trades || []).some((t) => (t.name || '').toLowerCase().includes(q));
    });
  };

  const filteredCrews = useMemo(
    () => (crews || []).filter(crewMatchesSearch),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [crews, crewSearch]
  );

  const crewStatusById = useMemo(() => {
    const map = new Map();
    const now = new Date();
    const nowTs = now.getTime();

    const formatDateTime = (dt) => dt.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });

    const normalizeRange = (start, end) => {
      const s = new Date(start);
      const e = new Date(end);
      if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return null;
      const endIsMidnight = (
        e.getHours() === 0 &&
        e.getMinutes() === 0 &&
        e.getSeconds() === 0 &&
        e.getMilliseconds() === 0
      );
      if (endIsMidnight) e.setHours(23, 59, 59, 999);
      return { start: s, end: e };
    };

    (crews || []).forEach((crew) => {
      const manualRanges = (crew.unavailability || [])
        .map((u) => ({ ...u, normalized: normalizeRange(u.start, u.end) }))
        .filter((u) => u.normalized);

      const scheduledRanges = (events || [])
        .filter((ev) => {
          const evCrewId = ev?.crew?._id || ev?.crew;
          if (!evCrewId || String(evCrewId) !== String(crew._id)) return false;
          if (!ev.start || !ev.end) return false;
          return true;
        })
        .map((ev) => ({ ...ev, normalized: normalizeRange(ev.start, ev.end) }))
        .filter((ev) => ev.normalized);

      const activeManual = manualRanges.find((u) => {
        const s = u.normalized.start.getTime();
        const e = u.normalized.end.getTime();
        return nowTs >= s && nowTs <= e;
      });

      const activeBooked = scheduledRanges.find((ev) => {
        const s = ev.normalized.start.getTime();
        const e = ev.normalized.end.getTime();
        return nowTs >= s && nowTs <= e;
      });

      const busyIntervals = [
        ...manualRanges.map((u) => ({ start: u.normalized.start, end: u.normalized.end })),
        ...scheduledRanges.map((ev) => ({ start: ev.normalized.start, end: ev.normalized.end }))
      ].sort((a, b) => a.start - b.start);

      let nextAvailableTs = nowTs;
      for (const interval of busyIntervals) {
        const s = interval.start.getTime();
        const e = interval.end.getTime();
        if (Number.isNaN(s) || Number.isNaN(e)) continue;
        if (e <= nextAvailableTs) continue;
        if (s > nextAvailableTs) break;
        nextAvailableTs = e;
      }

      const nextAvailableLabel = nextAvailableTs <= nowTs ? 'Now' : formatDateTime(new Date(nextAvailableTs));

      const futureStarts = [
        ...manualRanges
          .map((u) => u.normalized.start)
          .filter((d) => d.getTime() > nowTs),
        ...scheduledRanges
          .map((ev) => ev.normalized.start)
          .filter((d) => d.getTime() > nowTs)
      ].sort((a, b) => a - b);

      const nextStart = futureStarts[0] || null;

      if (activeManual) {
        map.set(String(crew._id), {
          tone: 'blocked',
          label: 'Unavailable',
          detail: activeManual.reason
            ? `Blocked: ${activeManual.reason}`
            : `Blocked until ${activeManual.normalized.end.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`,
          nextAvailableLabel
        });
        return;
      }

      if (activeBooked) {
        map.set(String(crew._id), {
          tone: 'booked',
          label: 'Booked',
          detail: `${activeBooked.title || 'Assigned'} until ${activeBooked.normalized.end.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`,
          nextAvailableLabel
        });
        return;
      }

      map.set(String(crew._id), {
        tone: 'available',
        label: 'Available',
        detail: nextStart
          ? `Next booked ${nextStart.toLocaleDateString()} ${nextStart.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
          : 'No upcoming conflicts',
        nextAvailableLabel
      });
    });

    return map;
  }, [crews, events]);

  const canManageCrews = ['super_admin', 'admin'].includes(role);

  return (
    <aside className="cs-root cs-root-v2">
      <div className="cs-top-bar">
        <div className="cs-top-title">Schedule</div>
        <div className="cs-top-actions">
          <button
            className="cs-icon-btn"
            onClick={onClose}
            title="Hide sidebar"
            aria-label="Hide sidebar"
          >
            <Icon name="close" size={14} />
          </button>
        </div>
      </div>

      <div className="cs-seg" role="tablist" aria-label="Sidebar sections">
        <button
          type="button"
          role="tab"
          aria-selected={activeSidebarTab === 'crews'}
          className={`cs-seg-btn ${activeSidebarTab === 'crews' ? 'is-active' : ''}`}
          onClick={() => setActiveSidebarTab('crews')}
        >
          <Icon name="users" size={13} />
          <span>Crews</span>
          <span className="cs-seg-count">{crews.length}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeSidebarTab === 'queue'}
          className={`cs-seg-btn ${activeSidebarTab === 'queue' ? 'is-active' : ''}`}
          onClick={() => setActiveSidebarTab('queue')}
        >
          <Icon name="pin" size={13} />
          <span>Projects</span>
          <span className="cs-seg-count">{filteredQueue.length}</span>
        </button>
      </div>

      {activeSidebarTab === 'crews' && (
        <div className="cs-pane">
          {canManageCrews && (
            <button
              type="button"
              className="cs-manage-btn cs-manage-btn-v2"
              onClick={() => onManageCalendars?.('crews')}
              title="Manage crews, people & trades"
            >
              <Icon name="settings" size={13} />
              <span>Manage Crews &amp; Trades</span>
            </button>
          )}

          <div className="cs-pane-tools">
            <div className="cs-pane-search">
              <Icon name="search" size={12} />
              <input
                placeholder="Search crews, trades, people"
                value={crewSearch}
                onChange={(e) => setCrewSearch(e.target.value)}
              />
              {crewSearch && (
                <button
                  type="button"
                  className="cs-pane-search-clear"
                  onClick={() => setCrewSearch('')}
                  title="Clear"
                  aria-label="Clear search"
                >
                  <Icon name="close" size={11} />
                </button>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 2 }}>
            <div className="cs-pane-hint" style={{ margin: 0 }}>Drag a crew onto a date to schedule them.</div>
            <button
              type="button"
              className={`cs-trades-toggle ${hideTrades ? '' : 'is-active'}`}
              onClick={(e) => { e.stopPropagation(); onToggleHideTrades?.(); }}
              title={hideTrades ? 'Show trades on crews & events' : 'Hide trades from crews & events'}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {hideTrades
                  ? <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" /><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" /><line x1="1" y1="1" x2="23" y2="23" /></>
                  : <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>
                }
              </svg>
              {hideTrades ? 'Trades' : 'Trades'}
            </button>
          </div>

          {selectedCrew && (
            <div className="cs-crew-filter-note">
              <span className="cs-crew-filter-text">Filtering to <strong>{selectedCrew.name}</strong></span>
              <button
                type="button"
                className="cs-pill-btn"
                onClick={(e) => { e.stopPropagation(); onCrewSelect?.(null); }}
              >
                Clear
              </button>
            </div>
          )}

          {crews.length === 0 ? (
            <div className="cs-empty">
              <div className="cs-empty-text">No crews yet</div>
              {canManageCrews && (
                <button className="cs-link" onClick={() => onManageCalendars?.('crews')}>+ Add your first crew</button>
              )}
            </div>
          ) : filteredCrews.length === 0 ? (
            <div className="cs-empty">
              <div className="cs-empty-text">No crews match "{crewSearch.trim()}"</div>
              <div className="cs-empty-sub">Try searching by name, trade, or a crew member.</div>
            </div>
          ) : (
            <div className="cs-crew-group-list">
              {filteredCrews.map((crew) => {
                const status = crewStatusById.get(String(crew._id)) || { tone: 'available', label: 'Available' };
                const isSelected = selectedCrew && String(selectedCrew._id) === String(crew._id);
                const trades = (crew.trades || []).filter((t) => t && (t.name || t._id));
                const people = (crew.people || []).filter((p) => p && (p.name || p._id));
                return (
                  <div
                    key={crew._id}
                    className={`cs-crew-pill ${isSelected ? 'is-selected' : ''}`}
                    onClick={() => onCrewSelect?.(isSelected ? null : crew._id)}
                    role="button"
                    tabIndex={0}
                    draggable
                    onDragStart={(e) => {
                      const payload = { ...crew, __isCrew: true };
                      window.__draggedCrewItem = payload;
                      onCrewDragStart?.(payload);
                      e.dataTransfer.setData('text/plain', String(crew._id));
                      e.dataTransfer.effectAllowed = 'copyMove';
                    }}
                    onDragEnd={() => {
                      window.__draggedCrewItem = null;
                      onCrewDragEnd?.();
                    }}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onCrewSelect?.(isSelected ? null : crew._id); } }}
                    title={`Drag ${crew.name} onto a date to schedule the crew`}
                  >
                    <span className="cs-crew-pill-handle" aria-hidden="true">
                      <Icon name="drag" size={12} />
                    </span>
                    <span className="cs-crew-pill-dot" style={{ background: crew.color || '#6366F1' }} />
                    <div className="cs-crew-pill-info">
                      <div className="cs-crew-pill-row">
                        <span className="cs-crew-pill-name">{crew.name}</span>
                        <span className={`cs-crew-pill-status is-${status.tone}`}>{status.label}</span>
                      </div>
                      {!hideTrades && trades.length > 0 && (
                        <div className="cs-crew-pill-trades">
                          {trades.map((t) => (
                            <span
                              key={String(t._id || t)}
                              className="cs-crew-pill-trade"
                              title={t.name || ''}
                            >
                              {t.name || 'Trade'}
                            </span>
                          ))}
                        </div>
                      )}
                      {people.length > 0 && (
                        <div className="cs-crew-pill-people">
                          {people.map((p) => {
                            const pTrades = (p.trades || []).filter((t) => t && (t.name || t._id));
                            const tradeText = pTrades.map((t) => t.name).filter(Boolean).join(', ');
                            return (
                              <div
                                key={String(p._id || p)}
                                className="cs-crew-pill-person"
                                title={tradeText ? `${p.name || 'Person'} — ${tradeText}` : (p.name || 'Person')}
                              >
                                <span className="cs-crew-pill-person-name">{p.name || 'Person'}</span>
                                {!hideTrades && pTrades.length > 0 && (
                                  <span className="cs-crew-pill-person-badges">
                                    {pTrades.map((t) => (
                                      <span
                                        key={String(t._id || t)}
                                        className="cs-crew-pill-trade cs-crew-pill-trade-person"
                                        title={t.name || ''}
                                      >
                                        {t.name || 'Trade'}
                                      </span>
                                    ))}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeSidebarTab === 'queue' && (
        <div className="cs-pane">
          <div className="cs-pane-tools">
            <div className="cs-pane-search">
              <Icon name="search" size={12} />
              <input
                placeholder="Search projects, PM, location"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button
                  type="button"
                  className="cs-pane-search-clear"
                  onClick={() => setSearch('')}
                  title="Clear"
                  aria-label="Clear search"
                >
                  <Icon name="close" size={11} />
                </button>
              )}
            </div>
          </div>

          <div className="cs-pane-tools cs-pane-tools-row">
            <select
              className="cs-q-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Filter projects by status"
            >
              <option value="ready_to_schedule">Ready to Schedule</option>
              <option value="active">Active</option>
              <option value="on_hold">On Hold</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="archived">Archived</option>
            </select>
            <button
              type="button"
              className="cs-icon-btn"
              onClick={() => dispatch(fetchProjectQueue(statusFilter))}
              title="Refresh"
              aria-label="Refresh"
            >
              <Icon name="refresh" size={12} />
            </button>
          </div>

          <div className="cs-pane-hint">Drag a project card onto a date to schedule it.</div>

          {selectedCrew && (
            <div className="cs-crew-filter-note">
              <span className="cs-crew-filter-text">Filtering to <strong>{selectedCrew.name}</strong></span>
              <button
                type="button"
                className="cs-pill-btn"
                onClick={(e) => { e.stopPropagation(); onCrewSelect?.(null); }}
              >
                Clear
              </button>
            </div>
          )}

          <div className="cs-q-list">
            {queueLoading && <div className="cs-empty"><div className="cs-empty-text">Loading…</div></div>}
            {!queueLoading && filteredQueue.length === 0 && (
              <div className="cs-empty">
                <div className="cs-empty-text">
                  {search.trim()
                    ? `No projects match "${search.trim()}"`
                    : selectedCrew
                      ? `No projects linked to ${selectedCrew.name}`
                      : 'No projects in queue'}
                </div>
                <div className="cs-empty-sub">
                  {search.trim()
                    ? 'Try a project name, project manager, or city'
                    : selectedCrew
                      ? 'Select another crew or clear the filter to view all projects'
                      : 'Drag a project onto a date to schedule it'}
                </div>
              </div>
            )}
            {filteredQueue.map((p) => {
              const priority = (p.priority || 'medium').toLowerCase().replace(/\s/g, '');
              const pmNames = (p.projectManagers || [])
                .map((pm) => {
                  const first = pm?.profile?.firstName || pm?.firstName || '';
                  const last = pm?.profile?.lastName || pm?.lastName || '';
                  return `${first} ${last}`.trim();
                })
                .filter(Boolean);
              const pmLabel = pmNames.length === 0
                ? null
                : pmNames.length === 1
                  ? pmNames[0]
                  : `${pmNames[0]} +${pmNames.length - 1}`;
              return (
                <div
                  key={p._id}
                  className={`cs-q-item ${p.isScheduled ? 'is-scheduled' : ''} prio-${priority}`}
                  draggable
                  onDragStart={(e) => onDragStart(e, p)}
                  onDragEnd={() => {
                    window.__draggedQueueItem = null;
                    onQueueDragEnd?.();
                  }}
                  title="Drag onto a date to schedule"
                >
                  <div className="cs-q-handle"><Icon name="drag" size={12} /></div>
                  <div className="cs-q-body">
                    <div className="cs-q-title">{p.title}</div>
                    <div className="cs-q-meta">
                      <span className={`cs-q-prio prio-${priority}`}>{p.priority || 'medium'}</span>
                      {pmLabel && (
                        <span className="cs-q-pm" title={pmNames.join(', ')}>PM: {pmLabel}</span>
                      )}
                      {p.address?.city && (
                        <span className="cs-q-loc">{p.address.city}{p.address.state ? `, ${p.address.state}` : ''}</span>
                      )}
                      {p.isScheduled && (
                        <span className="cs-q-status">{p.scheduledCount} scheduled</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </aside>
  );
};

export default CalendarSidebar;
